/**
 * Region Management — Settings section.
 * Admin/Scheduler can create, edit, archive regions and assign auditors.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { MapPin, Plus, Archive, RotateCcw, Save, Users, History } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';
import { getApiError } from '@/lib/utils';
import { invalidateRegionCache } from './RegionFilter';

export default function RegionSettings() {
  const [regions, setRegions] = useState([]);
  const [auditors, setAuditors] = useState([]);
  const [unassignedCount, setUnassignedCount] = useState(0);
  const [showArchived, setShowArchived] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editRegion, setEditRegion] = useState(null);
  const [form, setForm] = useState({ name: '', code: '', description: '' });
  const [auditLog, setAuditLog] = useState([]);
  const [showLog, setShowLog] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [regRes, audRes] = await Promise.all([
        api.get('/regions', { params: { include_archived: showArchived } }),
        api.get('/regions/auditors'),
      ]);
      setRegions(regRes.data.regions || []);
      setUnassignedCount(regRes.data.unassigned_auditor_count || 0);
      setAuditors(audRes.data || []);
    } catch (err) {
      toast.error(getApiError(err, 'Failed to load regions'));
    }
  }, [showArchived]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editRegion) {
        await api.patch(`/regions/${editRegion.id}`, form);
        toast.success('Region updated');
      } else {
        await api.post('/regions', form);
        toast.success('Region created');
      }
      invalidateRegionCache();
      setDialogOpen(false);
      setEditRegion(null);
      setForm({ name: '', code: '', description: '' });
      load();
    } catch (err) {
      toast.error(getApiError(err, 'Failed to save region'));
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async (region) => {
    if (!window.confirm(`Archive "${region.name}"? All auditors will be unassigned from this region.`)) return;
    try {
      await api.post(`/regions/${region.id}/archive`);
      toast.success(`"${region.name}" archived`);
      invalidateRegionCache();
      load();
    } catch (err) {
      toast.error(getApiError(err, 'Failed to archive'));
    }
  };

  const handleRestore = async (region) => {
    try {
      await api.post(`/regions/${region.id}/restore`);
      toast.success(`"${region.name}" restored`);
      invalidateRegionCache();
      load();
    } catch (err) {
      toast.error(getApiError(err, 'Failed to restore'));
    }
  };

  const handleAssign = async (auditorId, regionId) => {
    try {
      await api.post('/regions/assign-auditors', {
        assignments: [{ auditor_id: auditorId, region_id: regionId || null }],
      });
      toast.success('Auditor region updated');
      load();
    } catch (err) {
      toast.error(getApiError(err, 'Failed to assign'));
    }
  };

  const loadAuditLog = async () => {
    try {
      const res = await api.get('/regions/audit-log', { params: { limit: 50 } });
      setAuditLog(res.data || []);
      setShowLog(true);
    } catch (err) {
      toast.error(getApiError(err, 'Failed to load audit log'));
    }
  };

  const openCreate = () => {
    setEditRegion(null);
    setForm({ name: '', code: '', description: '' });
    setDialogOpen(true);
  };

  const openEdit = (region) => {
    setEditRegion(region);
    setForm({ name: region.name, code: region.code || '', description: region.description || '' });
    setDialogOpen(true);
  };

  const activeRegions = regions.filter(r => !r.is_archived);
  const archivedRegions = regions.filter(r => r.is_archived);

  return (
    <Accordion type="single" collapsible className="mb-4">
      <AccordionItem value="regions" className="border rounded-lg">
        <AccordionTrigger className="px-4 hover:no-underline" data-testid="regions-settings-trigger">
          <div className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-teal-600" />
            <span className="font-semibold">Regions</span>
            <Badge variant="outline" className="ml-2 text-xs">{activeRegions.length} active</Badge>
          </div>
        </AccordionTrigger>
        <AccordionContent className="px-4 pb-4">
          {/* Actions bar */}
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <Button size="sm" onClick={openCreate} data-testid="create-region-btn">
              <Plus className="w-4 h-4 mr-1" /> New Region
            </Button>
            <Button size="sm" variant="outline" onClick={loadAuditLog} data-testid="region-audit-log-btn">
              <History className="w-4 h-4 mr-1" /> Audit Log
            </Button>
            <label className="flex items-center gap-1.5 text-xs text-slate-500 ml-auto cursor-pointer">
              <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} className="rounded" />
              Show archived
            </label>
          </div>

          {/* Region cards */}
          <div className="space-y-2 mb-6">
            {activeRegions.map(r => (
              <div key={r.id} className="flex items-center justify-between py-2 px-3 border rounded-md hover:bg-slate-50" data-testid={`region-row-${r.id}`}>
                <div>
                  <span className="font-medium text-sm">{r.name}</span>
                  {r.code && <Badge variant="outline" className="ml-2 text-[10px]">{r.code}</Badge>}
                  <span className="text-xs text-slate-400 ml-3">{r.auditor_count} auditor{r.auditor_count !== 1 ? 's' : ''}</span>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => openEdit(r)} data-testid={`edit-region-${r.id}`}>Edit</Button>
                  <Button size="sm" variant="ghost" className="h-7 text-xs text-amber-600" onClick={() => handleArchive(r)} data-testid={`archive-region-${r.id}`}>
                    <Archive className="w-3 h-3 mr-1" />Archive
                  </Button>
                </div>
              </div>
            ))}
            {activeRegions.length === 0 && (
              <p className="text-sm text-slate-400 py-4 text-center">No regions configured. Create your first region above.</p>
            )}
            {showArchived && archivedRegions.length > 0 && (
              <div className="mt-4 pt-3 border-t">
                <p className="text-xs text-slate-400 mb-2 font-medium">Archived</p>
                {archivedRegions.map(r => (
                  <div key={r.id} className="flex items-center justify-between py-1.5 px-3 opacity-60">
                    <span className="text-sm line-through">{r.name}</span>
                    <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => handleRestore(r)}>
                      <RotateCcw className="w-3 h-3 mr-1" />Restore
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Auditor assignments */}
          <Card>
            <CardHeader className="py-3 px-4">
              <CardTitle className="text-sm flex items-center gap-2">
                <Users className="w-4 h-4" /> Auditor Region Assignments
              </CardTitle>
              <CardDescription className="text-xs">Assign each auditor to a region. Unassigned: {unassignedCount}</CardDescription>
            </CardHeader>
            <CardContent className="px-4 pb-3">
              <div className="space-y-1.5 max-h-64 overflow-y-auto">
                {auditors.map(a => (
                  <div key={a.id} className="flex items-center justify-between py-1" data-testid={`auditor-region-row-${a.id}`}>
                    <span className="text-sm">{a.first_name} {a.last_name}</span>
                    <Select
                      value={a.region_id ? String(a.region_id) : 'none'}
                      onValueChange={(v) => handleAssign(a.id, v === 'none' ? null : parseInt(v, 10))}
                    >
                      <SelectTrigger className="w-[160px] h-8 text-xs" data-testid={`auditor-region-select-${a.id}`}>
                        <SelectValue placeholder="Unassigned" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Unassigned</SelectItem>
                        {activeRegions.map(r => (
                          <SelectItem key={r.id} value={String(r.id)}>{r.code ? `${r.code} — ${r.name}` : r.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Create/Edit Dialog */}
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>{editRegion ? 'Edit Region' : 'Create Region'}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 py-2">
                <div>
                  <Label>Name *</Label>
                  <Input value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. North West" data-testid="region-name-input" />
                </div>
                <div>
                  <Label>Code</Label>
                  <Input value={form.code} onChange={(e) => setForm(f => ({ ...f, code: e.target.value }))} placeholder="e.g. NW" maxLength={10} data-testid="region-code-input" />
                </div>
                <div>
                  <Label>Description</Label>
                  <Input value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Optional notes" data-testid="region-desc-input" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleSave} disabled={!form.name.trim() || saving} data-testid="region-save-btn">
                  <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving...' : 'Save'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Audit Log Dialog */}
          <Dialog open={showLog} onOpenChange={setShowLog}>
            <DialogContent className="sm:max-w-lg max-h-[70vh]">
              <DialogHeader>
                <DialogTitle>Region Audit Log</DialogTitle>
              </DialogHeader>
              <div className="overflow-y-auto max-h-[50vh] space-y-1.5">
                {auditLog.length === 0 && <p className="text-sm text-slate-400 text-center py-4">No audit entries yet.</p>}
                {auditLog.map(entry => (
                  <div key={entry.id} className="text-xs border-b pb-1.5">
                    <div className="flex justify-between">
                      <Badge variant="outline" className="text-[9px]">{entry.action}</Badge>
                      <span className="text-slate-400">{entry.performed_at ? new Date(entry.performed_at).toLocaleString() : ''}</span>
                    </div>
                    <p className="text-slate-600 mt-0.5">
                      {entry.region_name && <span className="font-medium">{entry.region_name}</span>}
                      {entry.auditor_name && <span> — {entry.auditor_name}</span>}
                    </p>
                    <p className="text-slate-400">{entry.performed_by_name}</p>
                  </div>
                ))}
              </div>
            </DialogContent>
          </Dialog>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}
